// src/components/routing/zoneGeo.js
// --- UPDATE (Field Ops · Zones v1) — NEW FILE ---------------------------------------------
// Shared zone / constituency helpers for the web app.
//   ZONE_PALETTE + assignZoneColors()  — identical to backend services/fieldOpsZoneIntelService.js
//     so a zone has the same colour on every screen (stored colour wins, otherwise the next
//     unused palette colour in id order, then generated distinct colours — never two zones with
//     the same colour, even for all 39 constituencies).
//   parseGeo / pointInGeometry / locateZone — client-side point-in-polygon (e.g. preview).
//   PRIORITY_COLORS, RISK_LEVELS, zoneLabel(), fmt helpers ("—" when a value is unknown).
// --- END UPDATE ---------------------------------------------------------------------------

export const ZONE_PALETTE = Object.freeze([
    '#2563eb', '#16a34a', '#ea580c', '#db2777', '#7c3aed', '#0891b2', '#ca8a04', '#dc2626',
    '#0d9488', '#9333ea', '#65a30d', '#c2410c', '#be185d', '#4f46e5', '#059669', '#b45309',
    '#e11d48', '#0284c7', '#a16207', '#6d28d9', '#15803d', '#9f1239', '#1d4ed8', '#047857',
]);
const HEX = /^#[0-9a-f]{6}$/i;
export const isHexColor = (c) => HEX.test(String(c || ''));

const hslHex = (h, sat, l) => {
    const s = sat / 100; const L = l / 100; const k = (n) => (n + h / 30) % 12; const a = s * Math.min(L, 1 - L);
    const f = (n) => Math.round(255 * (L - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1))))).toString(16).padStart(2, '0');
    return `#${f(0)}${f(8)}${f(4)}`;
};
const extraColor = (n) => hslHex((n * 137.508 + 17) % 360, 68, [42, 52, 36][n % 3]);

/** Map(zoneId → colour). Same algorithm as the backend: no two zones share a colour, however many. */
export function assignZoneColors(zones = []) {
    const used = new Set(zones.map((z) => (isHexColor(z.color) ? z.color.toLowerCase() : null)).filter(Boolean));
    const free = ZONE_PALETTE.filter((c) => !used.has(c));
    let i = 0; let extra = 0;
    const next = () => {
        if (i < free.length) return free[i++];
        let c; do { c = extraColor(extra++); } while (used.has(c));
        return c;
    };
    const order = [...zones].sort((a, b) => String(a.id).localeCompare(String(b.id), undefined, { numeric: true }));
    const out = new Map();
    order.forEach((z) => {
        if (isHexColor(z.color)) out.set(z.id, z.color.toLowerCase());
        else { const c = next(); used.add(c); out.set(z.id, c); }
    });
    return out;
}

export const parseGeo = (raw) => {
    if (!raw) return null;
    let g = raw;
    if (typeof g === 'string') { try { g = JSON.parse(g); } catch (_) { return null; } }
    if (g && g.type === 'FeatureCollection') g = (g.features || [])[0]?.geometry || null;
    if (g && g.type === 'Feature') g = g.geometry;
    if (!g || !['Polygon', 'MultiPolygon'].includes(g.type) || !Array.isArray(g.coordinates)) return null;
    return g;
};
const polygonsOf = (g) => (g.type === 'Polygon' ? [g.coordinates] : g.coordinates);
const inRing = (x, y, ring) => {
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const [xi, yi] = ring[i]; const [xj, yj] = ring[j];
        if (((yi > y) !== (yj > y)) && (x < ((xj - xi) * (y - yi)) / ((yj - yi) || 1e-12) + xi)) inside = !inside;
    }
    return inside;
};
export const pointInGeometry = (lng, lat, g) => !!g && polygonsOf(g).some((p) => p.length && inRing(lng, lat, p[0]) && !p.slice(1).some((h) => inRing(lng, lat, h)));
/** First zone (smallest bbox when overlapping) containing lat/lng, or null. */
export function locateZone(zones, lat, lng) {
    let best = null;
    (zones || []).forEach((z) => {
        const g = z.geometry || parseGeo(z.polygonGeoJSON);
        if (g && pointInGeometry(lng, lat, g)) {
            const a = z.area_km2 ?? Infinity;
            if (!best || a < best.a) best = { z, a };
        }
    });
    return best ? best.z : null;
}

export const PRIORITY_COLORS = Object.freeze({ critical: '#e53e3e', high: '#dd6b20', medium: '#d69e2e', low: '#38a169' });
export const PRIORITY_SCHEME = Object.freeze({ critical: 'red', high: 'orange', medium: 'yellow', low: 'green' });
export const RISK_LEVELS = Object.freeze({
    critical: { scheme: 'red', label: 'Critical' }, high: { scheme: 'orange', label: 'High' },
    medium: { scheme: 'yellow', label: 'Medium' }, low: { scheme: 'green', label: 'Low' },
});
export const zoneLabel = (z) => (!z ? '—' : z.display_name || (z.constituency && z.constituency !== z.name ? `${z.name} · ${z.constituency}` : z.name));
export const dash = (v, suffix = '') => (v === null || v === undefined || Number.isNaN(v) ? '—' : `${v}${suffix}`);
export const ago = (iso) => {
    if (!iso) return '—';
    const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
    if (!Number.isFinite(m)) return '—';
    if (m < 1) return 'just now'; if (m < 60) return `${m} min ago`;
    const h = Math.round(m / 60); if (h < 48) return `${h} h ago`;
    return `${Math.round(h / 24)} d ago`;
};
/** Readable text colour (black/white) on a hex background. */
export const contrastText = (hex) => {
    if (!isHexColor(hex)) return 'white';
    const n = parseInt(hex.slice(1), 16); const r = n >> 16; const g = (n >> 8) & 255; const b = n & 255;
    return (r * 299 + g * 587 + b * 114) / 1000 > 150 ? '#1a202c' : 'white';
};
